import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateGuardrailInput } from '@eairlines/contracts';
import { NavLink } from 'react-router-dom';
import {
  getGuardrail,
  listGuardrails,
  runGuardrailTests,
  submitGuardrailForApproval,
  updateGuardrail,
} from '../api/client';
import { Icons } from '../components/icons';
import { StatusBadge } from '../components/status-badge';

const tabs = [
  ['Guardrails', '/guardrails'],
  ['Knowledge', '/knowledge'],
  ['Business rules', '/business-rules'],
  ['Conversation flow', '/conversation-flow'],
  ['Intent & wording', '/intent-wording'],
  ['Analytics', '/analytics'],
] as const;

export function GuardrailsPage() {
  const readOnlyPreview = import.meta.env.VITE_READ_ONLY_PREVIEW === 'true';
  const previewDisabledTitle = readOnlyPreview ? 'Unavailable in read-only preview' : undefined;
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['guardrails'], queryFn: listGuardrails });
  const [selectedId, setSelectedId] = useState<string>();
  const effectiveId = selectedId ?? list.data?.items[0]?.id;
  const detail = useQuery({
    queryKey: ['guardrail', effectiveId],
    queryFn: () => getGuardrail(effectiveId!),
    enabled: Boolean(effectiveId),
  });
  const [draft, setDraft] = useState<UpdateGuardrailInput>();
  const [severityFilter, setSeverityFilter] = useState<'ALL' | 'HIGH'>('ALL');
  const [showConflictDetails, setShowConflictDetails] = useState(false);
  const [showTestDetails, setShowTestDetails] = useState(false);

  useEffect(() => {
    if (!detail.data) return;
    setDraft({
      objective: detail.data.objective,
      severity: detail.data.severity,
      enforcement: detail.data.enforcement,
      instruction: detail.data.instruction,
      triggerConditions: detail.data.triggerConditions,
      prohibitedBehavior: detail.data.prohibitedBehavior,
      requiredBehavior: detail.data.requiredBehavior,
      fallbackResponse: detail.data.fallbackResponse,
      escalationRoute: detail.data.escalationRoute,
      changeReason: '',
      expectedVersion: detail.data.version,
    });
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () => updateGuardrail(effectiveId!, draft!),
    onSuccess: async (data) => {
      queryClient.setQueryData(['guardrail', effectiveId], data);
      await queryClient.invalidateQueries({ queryKey: ['guardrails'] });
    },
  });

  const tests = useMutation({
    mutationFn: () =>
      runGuardrailTests(effectiveId!, {
        expectedVersion: detail.data!.version,
        reason: 'Run required guardrail validation suite.',
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['guardrail', effectiveId], data);
      await queryClient.invalidateQueries({ queryKey: ['guardrails'] });
    },
  });

  const approval = useMutation({
    mutationFn: () =>
      submitGuardrailForApproval(effectiveId!, {
        expectedVersion: detail.data!.version,
        reason: 'Request approval after validation and review.',
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(['guardrail', effectiveId], data);
      await queryClient.invalidateQueries({ queryKey: ['guardrails'] });
    },
  });

  return (
    <main className="feature-page">
      <section className="page-heading">
        <div>
          <h1>Guardrails</h1>
          <p>
            Guardrails define the boundaries for safe and compliant assistant behavior. They are
            always enforced.
          </p>
        </div>
        <div className="page-actions">
          <button
            className="button secondary"
            disabled={readOnlyPreview || !detail.data || tests.isPending}
            title={previewDisabledTitle}
            onClick={() => tests.mutate()}
          >
            <Icons.Test size={17} /> {tests.isPending ? 'Running…' : 'Run tests'}
          </button>
          <button
            className="button primary"
            disabled={readOnlyPreview || !detail.data || approval.isPending}
            title={previewDisabledTitle}
            onClick={() => approval.mutate()}
          >
            <Icons.Approvals size={17} />{' '}
            {approval.isPending ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </section>
      <nav className="tabs" aria-label="Use case sections">
        {tabs.map(([tab, path]) => (
          <NavLink
            key={tab}
            to={path}
            className={({ isActive }) => (isActive ? 'tab active' : 'tab')}
          >
            {tab}
          </NavLink>
        ))}
      </nav>

      {readOnlyPreview ? (
        <div className="preview-notice" role="status">
          This hosted preview is read-only until the production identity provider is connected.
        </div>
      ) : null}
      {tests.isError ? (
        <p className="form-error" role="alert">
          Tests could not run: {tests.error.message}
        </p>
      ) : null}
      {approval.isError ? (
        <p className="form-error" role="alert">
          Approval could not be requested: {approval.error.message}
        </p>
      ) : null}

      {list.isError ? (
        <ErrorState title="Guardrails could not be loaded" error={list.error} />
      ) : null}
      {list.isPending ? <div className="loading">Loading governed configuration…</div> : null}
      {list.data ? (
        <div className="guardrail-grid">
          <section className="panel list-panel" aria-label="Guardrail list">
            <div className="panel-title-row">
              <h2>Guardrails ({list.data.total})</h2>
              <div>
                <button
                  className="filter-button"
                  onClick={() => setSeverityFilter(severityFilter === 'ALL' ? 'HIGH' : 'ALL')}
                >
                  <Icons.Sliders size={15} /> {severityFilter === 'ALL' ? 'All' : 'High'}{' '}
                  <Icons.ChevronDown size={14} />
                </button>
                <button
                  className="square-button"
                  aria-label="Add guardrail"
                  disabled
                  title="Guardrail creation is not enabled in this release."
                >
                  <Icons.Plus size={17} />
                </button>
              </div>
            </div>
            <div className="guardrail-list">
              {list.data.items
                .filter((item) => severityFilter === 'ALL' || item.severity === 'HIGH')
                .map((item) => (
                  <button
                    key={item.id}
                    className={
                      effectiveId === item.id ? 'guardrail-item selected' : 'guardrail-item'
                    }
                    onClick={() => setSelectedId(item.id)}
                  >
                    <span className="shield-icon">
                      <Icons.Shield size={20} />
                    </span>
                    <span className="guardrail-copy">
                      <strong>{item.name}</strong>
                      <span className="badges">
                        <StatusBadge value={item.severity} kind="severity" />
                        <StatusBadge value={item.enforcement} kind="enforcement" />
                      </span>
                    </span>
                    <StatusBadge value={item.inheritance} kind="inheritance" />
                    <Icons.ChevronRight size={16} />
                  </button>
                ))}
            </div>
            <div className="legend">
              <span>
                <i className="dot high" /> High
              </span>
              <span>
                <i className="dot medium" /> Medium
              </span>
              <span>
                <i className="dot low" /> Low
              </span>
            </div>
            <p className="panel-note">
              ⓘ Inherited guardrails come from a parent scope and cannot be edited.
            </p>
          </section>

          {detail.isPending ? (
            <section className="panel loading">Loading guardrail…</section>
          ) : null}
          {detail.isError ? (
            <ErrorState title="Guardrail details could not be loaded" error={detail.error} />
          ) : null}
          {detail.data && draft ? (
            <>
              <section className="panel editor-panel">
                <div className="panel-title-row">
                  <div>
                    <h2>{detail.data.name}</h2>
                    <StatusBadge value={detail.data.inheritance} kind="inheritance" />
                  </div>
                  <button
                    className="icon-button"
                    aria-label="More actions"
                    disabled={readOnlyPreview}
                    title={previewDisabledTitle}
                  >
                    ⋮
                  </button>
                </div>
                <label>
                  Plain-language objective
                  <textarea
                    disabled={readOnlyPreview || detail.data.inheritance === 'INHERITED'}
                    value={draft.objective}
                    onChange={(event) => setDraft({ ...draft, objective: event.target.value })}
                  />
                </label>
                <div className="field-row">
                  <label>
                    Severity
                    <select
                      disabled={readOnlyPreview || detail.data.inheritance === 'INHERITED'}
                      value={draft.severity}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          severity: event.target.value as UpdateGuardrailInput['severity'],
                        })
                      }
                    >
                      <option>CRITICAL</option>
                      <option>HIGH</option>
                      <option>MEDIUM</option>
                      <option>LOW</option>
                    </select>
                  </label>
                  <label>
                    Enforcement
                    <select
                      disabled={readOnlyPreview || detail.data.inheritance === 'INHERITED'}
                      value={draft.enforcement}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          enforcement: event.target.value as UpdateGuardrailInput['enforcement'],
                        })
                      }
                    >
                      <option>BLOCK</option>
                      <option>REDIRECT</option>
                      <option>REQUIRE_CONFIRMATION</option>
                      <option>REQUIRE_AUTHENTICATION</option>
                      <option>ESCALATE</option>
                      <option>WARN_INTERNAL</option>
                      <option>MONITOR</option>
                    </select>
                  </label>
                </div>
                <label>
                  Guardrail instruction
                  <textarea
                    disabled={readOnlyPreview || detail.data.inheritance === 'INHERITED'}
                    className="instruction"
                    value={draft.instruction}
                    onChange={(event) => setDraft({ ...draft, instruction: event.target.value })}
                  />
                  <span className="character-count">{draft.instruction.length} / 4000</span>
                </label>
                <div className="applies">
                  <strong>Applies when</strong>
                  <p>
                    {draft.triggerConditions.length
                      ? draft.triggerConditions.join(' · ')
                      : 'This guardrail applies to all conversations in this scope.'}
                  </p>
                  <button
                    className="text-button"
                    disabled={readOnlyPreview}
                    title={previewDisabledTitle}
                  >
                    Add conditions⌄
                  </button>
                </div>
                {detail.data.conflicts.map((conflict) => (
                  <div className={conflict.blocking ? 'alert warning' : 'alert'} key={conflict.id}>
                    <strong>△ {conflict.title}</strong>
                    <p>{conflict.description}</p>
                    <button
                      className="text-button"
                      onClick={() => setShowConflictDetails((value) => !value)}
                    >
                      View conflict details
                    </button>
                    {showConflictDetails ? (
                      <p>
                        <strong>Blocking:</strong> {conflict.blocking ? 'Yes' : 'No'} · Resolve with
                        the policy owner before publication.
                      </p>
                    ) : null}
                  </div>
                ))}
                <label>
                  Reason for change
                  <input
                    disabled={readOnlyPreview || detail.data.inheritance === 'INHERITED'}
                    placeholder="Required to create a new draft version"
                    value={draft.changeReason}
                    onChange={(event) => setDraft({ ...draft, changeReason: event.target.value })}
                  />
                </label>
                {save.isError ? <p className="form-error">{save.error.message}</p> : null}
                <button
                  className="button primary save-button"
                  disabled={
                    readOnlyPreview ||
                    detail.data.inheritance === 'INHERITED' ||
                    save.isPending ||
                    draft.changeReason.trim().length < 10
                  }
                  onClick={() => save.mutate()}
                >
                  <Icons.Save size={17} />{' '}
                  {detail.data.inheritance === 'INHERITED'
                    ? 'Create override to edit'
                    : readOnlyPreview
                      ? 'Read-only preview'
                      : save.isPending
                        ? 'Saving…'
                        : 'Save as new draft'}
                </button>
              </section>

              <section className="panel preview-panel">
                <div className="panel-title-row">
                  <h2>Platform-ready control</h2>
                  <span className={isReady(detail.data) ? 'ready-pill' : 'not-ready-pill'}>
                    {isReady(detail.data) ? '✓ Ready' : '△ Not ready'}
                  </span>
                </div>
                <div className={isReady(detail.data) ? 'success-banner' : 'readiness-warning'}>
                  <Icons.Shield size={21} />
                  <span>
                    <strong>
                      {isReady(detail.data)
                        ? 'This guardrail is eligible for the publication workflow.'
                        : 'This guardrail is not eligible for publication.'}
                    </strong>
                    <br />
                    {readinessExplanation(detail.data)}
                  </span>
                </div>
                <h3>Customer preview</h3>
                <div className="chat-preview">
                  <div className="customer-message">What is the arrival time of flight EY 101?</div>
                  <div className="assistant-message">
                    <span className="mini-shield">
                      <Icons.Shield size={15} />
                    </span>
                    {draft.fallbackResponse}
                  </div>
                  <small>This preview is an example and may vary in production.</small>
                </div>
                <div className="test-heading">
                  <h3>Test status</h3>
                  <span className="ready-pill">
                    ✓ {detail.data.testSummary.failed ? 'Failed' : 'Passed'}
                  </span>
                </div>
                <div className="test-stats">
                  <div>
                    <small>Total tests</small>
                    <strong>{detail.data.testSummary.total}</strong>
                  </div>
                  <div>
                    <small>Passed</small>
                    <strong className="green">{detail.data.testSummary.passed}</strong>
                  </div>
                  <div>
                    <small>Failed</small>
                    <strong className="red">{detail.data.testSummary.failed}</strong>
                  </div>
                  <div>
                    <small>Warnings</small>
                    <strong>{detail.data.testSummary.warnings}</strong>
                  </div>
                </div>
                <button
                  className="text-button"
                  onClick={() => setShowTestDetails((value) => !value)}
                >
                  View test results ↗
                </button>
                {showTestDetails ? (
                  <div className="finding">
                    <strong>Version {detail.data.version} validation</strong>
                    <p>
                      {detail.data.testSummary.passed} passed, {detail.data.testSummary.failed}{' '}
                      failed, {detail.data.testSummary.warnings} warnings.
                    </p>
                  </div>
                ) : null}
              </section>
            </>
          ) : null}
        </div>
      ) : null}
    </main>
  );
}

function isReady(guardrail: {
  conflicts: Array<{ blocking: boolean }>;
  testSummary: { total: number; passed: number; failed: number };
}): boolean {
  return (
    !guardrail.conflicts.some((conflict) => conflict.blocking) &&
    guardrail.testSummary.total > 0 &&
    guardrail.testSummary.failed === 0 &&
    guardrail.testSummary.passed === guardrail.testSummary.total
  );
}

function readinessExplanation(guardrail: {
  deterministicControl: string | null;
  conflicts: Array<{ blocking: boolean }>;
  testSummary: { total: number; failed: number };
}): string {
  if (guardrail.conflicts.some((conflict) => conflict.blocking))
    return 'Resolve all blocking conflicts before requesting approval.';
  if (guardrail.testSummary.total === 0)
    return 'Run the required tests for this exact version before requesting approval.';
  if (guardrail.testSummary.failed > 0) return 'Resolve failed tests before requesting approval.';
  return guardrail.deterministicControl ?? 'Runtime instruction enforcement required.';
}

function ErrorState({ title, error }: { title: string; error: Error }) {
  return (
    <section className="error-state" role="alert">
      <strong>{title}</strong>
      <p>{error.message}</p>
      <button className="button secondary" onClick={() => window.location.reload()}>
        Retry
      </button>
    </section>
  );
}
